import { v4 as uuidv4 } from 'uuid';

import { IDecisionParams } from 'APIPackage/DecisionModule/interfaces/IDecisionParams';
import { IDecision } from 'APIPackage/DecisionModule/interfaces/IDecision';
import { IDecisionBasicDocument } from 'APIPackage/DecisionModule/interfaces/IDecisionBasicDocument';
import { convertViolatedRequirementsFormValuesToKnmData } from 'KnmErknmPackage/MainModule/common/convertViolatedRequirementsFormValuesToKnmData';
import { convertObjectsFormValueToKnmData } from 'KnmErknmPackage/MainModule/common/convertObjectsFormValueToKnmData';
import { convertInspectorsFormValuesToKnmDataOld } from 'KnmErknmPackage/MainModule/common/convertInspectorsFormValuesToKnmDataOld';
import { convertDocumentsFormValueToKnmData } from 'KnmErknmPackage/MainModule/common/convertDocumentsFormValueToKnmData';
import { convertJudgmentsFormValuesToKnmData } from 'KnmErknmPackage/MainModule/common/convertJudgmentsFormValuesToKnmData';
import { convertFormDateTimeValueToKnmDataDate } from 'KnmPackage/MainModule/common/convertFormDateTimeValueToKnmDataDate';
import { convertAgreementsFormValuesToData } from 'KnmErknmPackage/MainModule/common/convertAgreementsFormValuesToData';

import { IDecisionForm } from '../interfaces/IDecisionForm';
import { convertDecisionOrganizationFormValuesToDecisionData } from './convertDecisionOrganizationFormValuesToDecisionData';
import { convertEventsFormValuesToKnmData } from '../../../KnmErknmPackage/MainModule/common/convertEventsFormValuesToKnmData';
import { convertReasonsFormValueToDecisionData } from './convertReasonsFormValueToDecisionData';
import { IKnmEventUav } from '../../../APIPackage/KnmModule/interfaces/IKnmEventUav';

export const convertDecisionFormValuesToCreateParams = (
  values: IDecisionForm,
  decisionData: IDecision | undefined,
  isDraft?: boolean,
): IDecisionParams => {
  const {
    kind,
    kindControl,
    knoOrganization,
    basisDocumentDateTime,
    basisDocumentNumber,
    prosecutorOrganization,
    objects,
    inspectors,
    documents,
    additionalDocuments,
    appealStatus,
    organization,
    isTest,
    isOrganizationNew,
  } = values;

  const getDecisionBasisDocument = (
    dateTime: string | undefined,
    number: string | undefined,
    basisDocument?: IDecisionBasicDocument | null,
  ): IDecisionBasicDocument | null => {
    if (basisDocument) {
      return basisDocument;
    }

    if (dateTime && number) {
      const convertedDate = convertFormDateTimeValueToKnmDataDate(dateTime);

      if (convertedDate) {
        return {
          dateTime: convertedDate,
          number,
        };
      }
    }

    return null;
  };

  return {
    id: decisionData?.id ?? null,
    number: decisionData?.number ?? null,
    guid: decisionData?.guid ?? null,
    status: decisionData?.status ?? null,
    prosecutorOrganization: {
      id: !prosecutorOrganization?.ervkUuid && prosecutorOrganization?.id ? prosecutorOrganization?.id : null,
      code: null,
      name: prosecutorOrganization?.fullName || null,
      address: null,
      contacts: null,
      federalDistrictId: null,
      regionId: null,
      parentId: null,
      enabled: null,
      ervkUuid: prosecutorOrganization?.ervkUuid || decisionData?.prosecutorOrganization?.ervkUuid,
      ervkVersionUuid: prosecutorOrganization?.ervkVersionUuid || decisionData?.prosecutorOrganization?.ervkVersionUuid,
    },
    knoOrganization: knoOrganization?.value ?? decisionData?.knoOrganization ?? null,
    kindControl: kindControl?.value ?? decisionData?.kindControl ?? null,
    kind: kind?.value ?? decisionData?.kind ?? null,
    basisDocument: getDecisionBasisDocument(basisDocumentDateTime, basisDocumentNumber, decisionData?.basisDocument),
    objects: convertObjectsFormValueToKnmData(objects, !decisionData, isDraft),
    inspectors: convertInspectorsFormValuesToKnmDataOld(inspectors),
    signatureInfo: decisionData?.signatureInfo ?? null,
    isAppealed: decisionData?.isAppealed ?? null,
    appealStatus: appealStatus?.value ?? null,
    subjectNotifications: decisionData?.subjectNotifications ?? null,
    deleted: decisionData?.deleted ?? false,
    deletedAt: decisionData?.deletedAt ?? null,
    draft: isDraft ? decisionData?.draft || null : null,
    draftSavedAt: isDraft ? decisionData?.draftSavedAt ?? null : null,
    signed: decisionData?.signed ?? null,
    signedAt: decisionData?.signedAt ?? null,
    createdBy: decisionData?.createdBy ?? null,
    createdAt: decisionData?.createdAt ?? null,
    updatedBy: decisionData?.updatedBy ?? null,
    updatedAt: decisionData?.updatedAt ?? null,
    accessPrivilege: decisionData?.accessPrivilege ?? null,
    district: {
      id: prosecutorOrganization?.districtId || decisionData?.district?.id || null,
      name: prosecutorOrganization?.districtName || decisionData?.district?.name || null,
      center: null,
      okatoTer: null,
      okatoKod1: null,
      okatoRazdel: null,
      federalRegionCode: null,
      federalDistrict: null,
    },
    violations: convertViolatedRequirementsFormValuesToKnmData(values.violatedMandatoryRequirements, isDraft),
    isViolations:
      typeof values.isViolatedMandatoryRequirements?.value === 'boolean'
        ? values.isViolatedMandatoryRequirements.value
        : null,
    isNeedToAddAct: typeof values.isNeedToAddAct?.value === 'boolean' ? values.isNeedToAddAct.value : null,
    organization: convertDecisionOrganizationFormValuesToDecisionData(organization, isOrganizationNew, isDraft),
    additionalDocuments: convertDocumentsFormValueToKnmData(additionalDocuments, isTest, isDraft),
    judgments: convertJudgmentsFormValuesToKnmData(values, isDraft),
    events: convertEventsFormValuesToKnmData(values.events, undefined, undefined, isDraft),
    accessToken: decisionData?.accessToken || null,
    publishedStatus: decisionData?.publishedStatus || null,
    isTest: isTest ?? null,
    resigningRequired: decisionData?.resigningRequired ?? null,
    documents: convertDocumentsFormValueToKnmData(documents, isTest, isDraft),
    reasons: convertReasonsFormValueToDecisionData(values),
    massCancel: decisionData?.massCancel || null,
    massDelete: decisionData?.massDelete || null,
    firstExtractDateTime: convertFormDateTimeValueToKnmDataDate(decisionData?.firstExtractDateTime, false) || null,
    agreements: convertAgreementsFormValuesToData(values.agreements),
    agreementOnApproval: values.agreementOnApproval || null,
    repeatPassports: values.repeatPassports || null,
    uavTechnologies:
      values?.uavTechnologies?.map(x => {
        const eventUav: IKnmEventUav = {
          guid: x.guid || uuidv4(),
          typeId: x.type?.id || null,
          typeCode: x.type?.name || null,
          name: x.name || null,
        };

        return eventUav;
      }) || null,
  };
};
